/**
 * מצב המערכת — the tile that led nowhere.
 *
 * The console offered "מצב הנתונים — מתי רצה השרשרת הלילית, מה עבר ומה נכשל, וטריות הנתונים לפי
 * חברה" and had no screen behind it. Two of those four things this build cannot measure at all, so
 * a screen that showed them would have been worse than the missing one.
 *
 * ── WHAT IS HERE ────────────────────────────────────────────────────────────────────────────────
 *
 *   · REFUSALS, grouped by the reason the gate recorded. This is the closest thing the product has
 *     to an incident feed and it is the one nobody could see: a run of "אין לך הרשאה" is a role
 *     nobody granted, and a run of portal refusals is a customer at the wrong front door.
 *   · SCHEDULED RUNS, from `audit.job_run`. Currently none, which is stated as none.
 *   · AUDIT INCIDENTS — R-8. When an audited operation's record could not be written, the operation
 *     was refused and the refusal is recorded here. It is the ONE section on this screen that does
 *     not come from the audit store, which is the point: an `audit_unavailable` incident is ABOUT
 *     that store, so evidence read from it would vanish exactly when it mattered (A-6(1)).
 *     Its LIMIT is printed beside it — per-process, lost on restart — because a list that implied
 *     completeness it does not have would be the fabricated state this screen exists to refuse.
 *   · WHAT IS NOT MEASURED, named. Connector health and per-company data freshness produce no rows
 *     in this build, and a tile showing them at zero would say nothing is wrong with a subsystem
 *     nothing can see.
 *
 * ── AND THE REASON CODE IS SHOWN BESIDE THE HEBREW ──────────────────────────────────────────────
 *
 * 09 §9.1 V3 forbids a raw code on the screen of the person who was REFUSED — a denial must not
 * teach an attacker which check failed. This is the opposite audience: the Owner reviewing their own
 * estate's refusals, where two different reasons collapsing into one uniform sentence would merge
 * two incidents into one row. The Hebrew leads; the code is secondary and LTR, exactly as a customer
 * slug is beside a customer name.
 */

import { useCallback } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { FactList } from '../components/FactList';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { DenialSummary, DenyReason, IncidentSummary, JobRunSummary } from '../api/contract';
import { DENY_REASONS } from '../api/contract';
import { denialTextFor } from '../api/denialText';
import { formatInstantHe } from '../api/adminVocabulary';
import type { Tone } from '../api/adminVocabulary';
/*
 * THE TWO LABEL MAPS MOVED TO `api/connectionText.ts` ON 2026-11-01, and the module's header says
 * why: the platform CONSOLE drew the same two facts from string literals and was therefore wrong
 * about both. One copy, two screens.
 */
import {
  MAIL_CHANNEL_HE,
  MAIL_CHANNEL_UNKNOWN_HE,
  PAYMENT_PROVIDER_HE,
  PAYMENT_PROVIDER_UNKNOWN_HE,
} from '../api/connectionText';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import type { ClearingAttemptView, ClearingOverviewView } from '../api/contract';
import { routes } from '../routes';

const TITLE_HE = 'מצב המערכת';

/** The codes the server may report as unmeasured, in the words of the business. */
const UNMEASURED_HE: Readonly<Record<string, string>> = {
  connectors:
    'חיבור למערכות הנהלת חשבונות בבתי הלקוחות — טרם הותקן מחבר אצל אף לקוח, ולכן אין תקינות חיבור למדוד.',
  company_data_freshness:
    'טריות הנתונים לפי חברה — עדיין לא נקלטו נתונים ממערכת חיצונית, ולכן אין תאריך קליטה להציג.',
};

/**
 * The two incident kinds, in the words of the business.
 *
 * This audience is the Owner reading their OWN estate, not a refused caller, so the kind is named
 * rather than made uniform — merging "the trail would not accept a row" into "the chain forked" would
 * merge two different jobs into one row. 09 §9.1 V3 governs the refused caller's screen, not this one.
 */
const INCIDENT_KIND_HE: Readonly<Record<string, string>> = {
  audit_unavailable:
    'יומן הביקורת לא קיבל את הרשומה — הפעולה סורבה ולא בוצעה. אין שינוי שנשמר בלי רישום.',
  audit_chain_fork:
    'שרשרת הביקורת קיבלה שתי רשומות עוקבות לאותה רשומה קודמת — הפעולה סורבה ולא בוצעה.',
};

function knownReason(code: string): DenyReason | undefined {
  const known: readonly string[] = DENY_REASONS;
  return known.includes(code) ? (code as DenyReason) : undefined;
}

export function PlatformHealthScreen(): JSX.Element {
  const ctx = useWorkContext();

  const read = useCallback(() => callOperation('platform.incidents.read', {}), []);
  const healthRead = useServerData(read);
  /*
   * ── THE CHARGES LEFT THIS SCREEN. **Owner, 2026-11-02.** ────────────────────────────────────
   *
   *     *"צריך להיות בקטגוריה נפרדת בבילינג ולא בבריאות המערכת — זה לא קשור לבריאות מערכת."*
   *
   * They are under חיובים now, in `ClearingPanel`. What stays here is the one clearing fact that
   * IS health — whether this estate has a provider configured at all, which decides whether
   * self-signup can complete — and it arrives with the health read as `paymentProvider`. This
   * screen no longer reads `platform.clearing.read` and no longer needs the capability.
   */

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  const denialColumns: readonly Column<DenialSummary>[] = [
    {
      key: 'reason',
      headerHe: 'הסיבה',
      render: (d) => (
        <span className="company-cell">
          <span className="company-cell__name">{denialTextFor(knownReason(d.denyReason)).titleHe}</span>
          <span className="company-cell__id" dir="ltr">
            {d.denyReason}
          </span>
        </span>
      ),
    },
    { key: 'count', headerHe: 'מספר מקרים', numeric: true, render: (d) => <span dir="ltr">{d.count}</span> },
    {
      key: 'last',
      headerHe: 'המקרה האחרון',
      render: (d) => <span dir="ltr">{formatInstantHe(d.lastOccurredAtUtc)}</span>,
    },
  ];

  const runColumns: readonly Column<JobRunSummary>[] = [
    { key: 'job', headerHe: 'התהליך', render: (r) => <span dir="ltr">{r.jobId}</span> },
    {
      key: 'started',
      headerHe: 'התחיל',
      render: (r) => <span dir="ltr">{formatInstantHe(r.startedAtUtc)}</span>,
    },
    {
      key: 'outcome',
      headerHe: 'התוצאה',
      render: (r) =>
        r.outcome === null ? (
          <StatusChip labelHe="עדיין רץ" tone="muted" />
        ) : r.outcome === 'succeeded' ? (
          <StatusChip labelHe="הסתיים בהצלחה" tone="ok" />
        ) : (
          <StatusChip labelHe="לא הסתיים כשורה" tone="warn" />
        ),
    },
    {
      key: 'artifact',
      headerHe: 'הוכחת תוצר',
      // G-17: a run that finished without asserting what it produced proves nothing, and that is a
      // fact about the run worth seeing beside its outcome.
      render: (r) =>
        r.assertedArtifact ? (
          <StatusChip labelHe="נרשם תוצר" tone="ok" />
        ) : (
          <StatusChip labelHe="לא נרשם תוצר" tone="warn" />
        ),
    },
  ];

  const incidentColumns: readonly Column<IncidentSummary>[] = [
    {
      key: 'occurred',
      headerHe: 'מתי',
      render: (i) => formatInstantHe(i.occurredAtUtc),
    },
    {
      key: 'kind',
      headerHe: 'מה קרה',
      render: (i) => (
        <>
          <span>{INCIDENT_KIND_HE[i.kind] ?? 'תקלה ביומן הביקורת.'}</span>{' '}
          <span className="breakdown__code" dir="ltr">
            {i.kind}
          </span>
        </>
      ),
    },
    {
      key: 'route',
      headerHe: 'המסלול',
      // `null` is a real answer: a request that matched no route can still fail an audit write.
      render: (i) => (
        <span dir="ltr">{i.routeId ?? '—'}</span>
      ),
    },
    {
      key: 'reference',
      headerHe: 'קוד לפנייה',
      // THE JOIN. This is the code the refused user was shown, so a support call reaches this row.
      render: (i) => (
        <span dir="ltr">{i.requestUid}</span>
      ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
    >
      <ServerRead result={healthRead.result} whatHe="את מצב המערכת" skeletonRows={6}>
        {(data) => (
          <>
            <section className="figures" aria-label="סיכום">
              <article className="figure">
                <p className="figure__label">אירועים שנרשמו</p>
                {/*
                  A NULL IS NOT A ZERO. When the audit store could not be read the server sends
                  `null`, and this renders an em dash rather than `0` — a zero here would say "no
                  events happened" about the one facility that could not be asked.
                */}
                <p className="figure__value" dir="ltr">
                  {data.health.totalEvents ?? '—'}
                </p>
                <p className="figure__note">מאז {formatInstantHe(data.health.sinceUtc)}</p>
              </article>
              <article className="figure">
                <p className="figure__label">מתוכם סירובים</p>
                <p className="figure__value" dir="ltr">
                  {data.health.totalDenials ?? '—'}
                </p>
              </article>
              {/*
                WHICH MAIL PATH THIS ESTATE COMPOSED — X7 item 1.

                IT IS IN THIS SECTION, ABOVE THE AUDIT-FACILITY BLOCK, ON PURPOSE. `mailChannel` is a
                fact about THIS PROCESS, carried in the `evidence` object both `auditFacility` return
                paths spread (`platform-health.ts:225-238, 265-297`), so it is read without touching
                `audit.*`. An unreadable audit store must not take this row down with the counts.

                And because it is a fact rather than a measurement it never renders `—` and never
                renders a zero: there is no state in which the server composed no mail port, since a
                0- or 2-`MailPort` census is a BOOT failure.
              */}
              <article className="figure">
                <p className="figure__label">נתיב הדואר היוצא</p>
                {/*
                  NOT `figure__value`. **Corrected 2026-10-28.** That class is the NUMERIC display
                  — 1.75rem, tabular figures — and it is right for a count. A chip inherited the
                  size and grew past the card it sits in, which is the *"תג שחורג מהכרטיס"* the
                  Owner saw. A state is a word, not a figure, so it is rendered at the card's own
                  text size and wraps inside it.
                */}
                <p className="figure__state">
                  <StatusChip
                    labelHe={(MAIL_CHANNEL_HE[data.health.mailChannel] ?? MAIL_CHANNEL_UNKNOWN_HE).valueHe}
                    tone={(MAIL_CHANNEL_HE[data.health.mailChannel] ?? MAIL_CHANNEL_UNKNOWN_HE).tone}
                  />
                </p>
                {/* The raw census name beside the Hebrew, LTR and secondary. The Hebrew leads. */}
                <p className="breakdown__code" dir="ltr">
                  {data.health.mailChannel}
                </p>
                <p className="figure__note">
                  {(MAIL_CHANNEL_HE[data.health.mailChannel] ?? MAIL_CHANNEL_UNKNOWN_HE).bodyHe}
                </p>
              </article>
              {/*
                WHICH PAYMENT PROVIDER THIS ESTATE COMPOSED. **Owner section 8, 2026-11-01.**

                Beside the mail row and built the same way, because it is the same KIND of fact: a
                property of this process, carried in `evidence`, so it survives an unreadable audit
                store. It is the port's own `provider` name read back from the object the
                composition built -- `none`, `sandbox`, or a provider that was actually contacted.

                NO ARM OF IT READS AS "CLEARING WORKS". Two of the three say in so many words that
                no money can move, which is the Owner's standing rule about this product:
                *"אין להציג יכולת מדומה כפעילה."*
              */}
              <article className="figure">
                <p className="figure__label">סליקה</p>
                <p className="figure__state">
                  <StatusChip
                    labelHe={
                      (PAYMENT_PROVIDER_HE[data.health.paymentProvider] ?? PAYMENT_PROVIDER_UNKNOWN_HE).valueHe
                    }
                    tone={
                      (PAYMENT_PROVIDER_HE[data.health.paymentProvider] ?? PAYMENT_PROVIDER_UNKNOWN_HE).tone
                    }
                  />
                </p>
                <p className="breakdown__code" dir="ltr">
                  {data.health.paymentProvider}
                </p>
                <p className="figure__note">
                  {(PAYMENT_PROVIDER_HE[data.health.paymentProvider] ?? PAYMENT_PROVIDER_UNKNOWN_HE).bodyHe}
                </p>
              </article>
            </section>

            {/*
              THE BACKING FACILITY'S OWN STATE — A-6(1). Not silence.

              This screen reads `audit.*`. When that read fails the page used to go down with it, so
              the one page an operator opens while the trail is refusing writes was the page that
              failed too. It now says so, and the incident section below still renders, because the
              incidents do not come from that store.
            */}
            {data.health.auditFacility === 'UNREADABLE' ? (
              <StateBlock
                titleHe="לא ניתן לקרוא את יומן הביקורת"
                bodyHe="הקריאה ליומן הביקורת של השרת נכשלה, ולכן אין כאן מספרים — לא אפס, אלא לא ידוע. רשימת התקלות שלמטה נקראת ממקור אחר וממשיכה להיות זמינה."
                tone="error"
              />
            ) : null}

            {data.health.denials.length === 0 ? (
              <StateBlock
                titleHe="לא נרשם אף סירוב בתקופה הזו"
                bodyHe="הקריאה לשרת הצליחה ולא נמצא סירוב אחד. זו תשובה על התקופה שנמדדה, ולא הצהרה שאין תקלות."
                tone="empty"
              />
            ) : (
              <DataTable<DenialSummary>
                captionHe="סירובים לפי סיבה"
                columns={denialColumns}
                rows={data.health.denials}
                rowKey={(d) => d.denyReason}
                filterSummaryHe={`מציג ${data.health.denials.length} סיבות סירוב`}
                emptyStateHe="אין סירובים."
              />
            )}

            {data.health.jobRuns.length === 0 ? (
              <StateBlock
                titleHe="לא נרשמה אף ריצה מתוזמנת"
                bodyHe="אין בגרסה הזו תהליך לילי שרץ, ולכן טבלת הריצות ריקה. זהו מצב אמיתי ולא כשל: כשיופעל תהליך מתוזמן, כל ריצה שלו תופיע כאן עם התוצאה שלה."
                tone="empty"
              />
            ) : (
              <DataTable<JobRunSummary>
                captionHe="ריצות מתוזמנות אחרונות"
                columns={runColumns}
                rows={data.health.jobRuns}
                rowKey={(r) => `${r.jobId}-${r.startedAtUtc}`}
                filterSummaryHe={`מציג ${data.health.jobRuns.length} ריצות אחרונות`}
                emptyStateHe="אין ריצות."
              />
            )}

            {/*
              ── AUDIT INCIDENTS — R-8's "surface alert/incident evidence" ──────────────────────

              The zero-state is written to be TRUE rather than reassuring: an empty list means this
              PROCESS has recorded none since it started, which is not the same statement as "none
              happened". The limit sits beside the list in both states, because a reader who sees an
              empty table and no caveat will read it as the estate's history.
            */}
            <section aria-label="תקלות ביומן הביקורת">
              {data.health.incidents.length === 0 ? (
                <StateBlock
                  titleHe="לא נרשמה תקלה ביומן הביקורת בתהליך הזה"
                  bodyHe="מאז שהשרת הזה עלה לא נרשמה אף פעולה שסורבה מפני שלא ניתן היה לרשום אותה ביומן. זו תשובה על התהליך הנוכחי בלבד."
                  tone="empty"
                />
              ) : (
                <DataTable<IncidentSummary>
                  captionHe="פעולות שסורבו מפני שלא ניתן היה לרשום אותן ביומן"
                  columns={incidentColumns}
                  rows={data.health.incidents}
                  rowKey={(i) => `${i.requestUid}-${i.kind}`}
                  filterSummaryHe={`מציג ${data.health.incidents.length} תקלות`}
                  emptyStateHe="אין תקלות."
                />
              )}
              {/*
                THE LIMIT, ALWAYS. In both states, and never collapsed into the empty case: the
                limit is a property of the register, not of whether it happens to be empty.

                AND THE LIMIT OF THE OTHER COPY TOO (F1). The earlier wording said the permanent
                record is in the system log. This process does not observe journald's retention or
                the unit's log rate limit, so it may not promise either: what it can state is that
                the line is WRITTEN OUT, and that the first incident of each kind gets its own line
                while the rest are counted (F2). An operator reading "the permanent record is in
                journald" would stop looking for a durable one.
              */}
              <p className="figure__note">
                הרשימה הזו נשמרת בזיכרון התהליך של השרת בלבד: היא נמחקת באתחול, ולכל מופע שרת יש
                רשימה משלו. התקלה הראשונה מכל סוג נכתבת גם לפלט השגיאות של התהליך, שהשרת מפנה ליומן
                המערכת (journald), ולאחריה נכתבות שורות מסכמות עם מספר התקלות ולא שורה לכל תקלה. משך
                השמירה ביומן המערכת והגבלת קצב הכתיבה אליו הם הגדרות של השרת, והמערכת הזו אינה רואה
                אותן — ולכן אין כאן הבטחה שהרשומה נשמרה.
                {data.health.incidentsDropped > 0
                  ? ` הרשימה מוגבלת בגודלה, ולכן ${data.health.incidentsDropped} תקלות ישנות כבר אינן מוצגות כאן — מתוך ${data.health.incidentsObserved} שנרשמו בתהליך הזה.`
                  : ` מתוך ${data.health.incidentsObserved} תקלות שנרשמו בתהליך הזה.`}
              </p>
            </section>

            {/*
              AND WHAT IS NOT MEASURED. Named one by one rather than summarised, because "some
              things are not measured" is a sentence that lets a reader assume the missing thing is
              not the one they care about.
            */}
            <section className="not-available" aria-label="מה שאינו נמדד">
              <h3 className="breakdown__title">מה שהמערכת אינה מודדת כרגע</h3>
              <dl className="not-available__list">
                {data.health.unmeasured.map((code) => (
                  <div key={code} className="not-available__item">
                    <dd>{UNMEASURED_HE[code] ?? 'תחום שאינו נמדד בגרסה הזו.'}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </>
        )}
      </ServerRead>
    </AppShell>
  );
}

